import Api, { tmdb, backend } from 'services/BaseService'
import { sanityClient, sanityClientFresh } from 'services/SanityClient'
// Interfaces
import { IGetListPayload, IDetailPayload } from '@/interfaces/IPayloads'
import axios from 'axios'
import { urlFor } from 'services/SanityImage'

// Get streaming data API from WatchMode
export const fetchMovieSources = async (tmdbId: string) => {
  if (!tmdbId) {
    throw new Error('No tmdbId provided to fetchMovieSources')
  }

  const baseURL = 'https://api.watchmode.com'
  const endpoint = `/v1/title/movie-${tmdbId}/sources/`
  const params = {
    apiKey: 'f0Xy1XtHAg6C6McbQxONiH5afgRV7zXb7J8urVTk',
    regions: 'CA',
  }

  try {
    const response = await axios.get(`${baseURL}${endpoint}`, { params })
    return response.data
  } catch (error) {
    throw new Error('Failed to fetch sources')
  }
}

/// Master endpoint from sanity for BFE
export const getBlueFoxMasterSanityData = async () => {
  const query = `*[_type == "bfe_master_movie"]
    | order(subheading.isHeroMovie desc, releaseDate desc){
      _id,
      tmdbId,
      title,
      slug,
      releaseDate,
      poster { asset->{ url } },
      backdrop { asset->{ url } },
      voteAverage,
      voteCount,
      subheading,
      genres,
      showInNewReleases,
      showInComingSoon,
      showStreamingAPI,
      streamingLinks[] {
        name,
        type,
        region,
        format,
        web_url,
        ios_url,
        android_url,
        price,
        source_id,
        priority,
        isOfficial
      },

      // 👇 Pick the first YouTube Trailer/Teaser key
      "youtubeTrailerId": coalesce(
        trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
      )
    }`

  const results = await sanityClient.fetch(query)

  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId ?? doc._id,
    title: doc.title,
    poster_path: doc.poster ? urlFor(doc.poster).url() : '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    slug: doc.slug?.current,
    _id: doc._id,
    genres: doc.genres || [],
    showInNewReleases: doc.showInNewReleases ?? false,
    showInComingSoon: doc.showInComingSoon ?? false,
    showStreamingAPI: doc.showStreamingAPI ?? false,
    streamingLinks: doc.streamingLinks || [],
    // 👇 IMPORTANT: include the trailer id
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  return {
    data: {
      items: normalized,
      results: normalized,
      total_pages: 1,
    },
  }
}

export const getMasterMovieFromSanityBySlug = async (slug: string | undefined) => {
  const query = `*[_type == "bfe_master_movie" && slug.current == $slug][0]{
    _id,
    "tmdb_id": tmdbId,
    title,
    slug,
    "release_date": releaseDate,
    overview,
    tagline,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    originalLanguage,
    spokenLanguages,
    genres,
    showInNewReleases,
    showInComingSoon,
    showStreamingAPI,
    trailers[] {
      key,
      site,
      name,
      type
    },
    streamingLinks[] {
      name,
      type,
      region,
      format,
      web_url,
      ios_url,
      android_url,
      price,
      source_id,
      priority,
      isOfficial
    }
  }`

  const result = await sanityClient.fetch(query, { slug })

  if (!result) return null

  return {
    id: result.tmdbId,
    tmdb_id: result.tmdb_id,
    title: result.title,
    slug: result.slug?.current,
    release_date: result.release_date,
    overview: result.overview,
    tagline: result.tagline,
    poster_path: result.poster ? urlFor(result.poster).url() : '',
    backdrop_path: result.backdrop ? urlFor(result.backdrop).url() : '',
    vote_average: result.voteAverage,
    vote_count: result.voteCount,
    genres: result.genres,
    original_language: result.originalLanguage,
    spoken_languages: result.spokenLanguages,
    trailers: result.trailers || [],
    streamingLinks: result.streamingLinks || [],
    showInNewReleases: result.showInNewReleases ?? false,
    showInComingSoon: result.showInComingSoon ?? false,
    showStreamingAPI: result.showStreamingAPI ?? false,
  }
}


/// Master canada endpoint from sanity


export const getBlueFoxCanadaSanityData = async () => {
  const query = `*[_type == "bfe_movie"]
    | order(releaseDate desc){
      _id,
      tmdbId,
      title,
      slug,
      releaseDate,
      poster { asset->{ url } },
      backdrop { asset->{ url } },
      voteAverage,
      voteCount,
      subheading,
      genres,
      showInNewReleases,
      showInComingSoon,
      showStreamingAPI,
      streamingLinks[] {
        name,
        type,
        region,
        format,
        web_url,
        ios_url,
        android_url,
        price,
        source_id,
        priority,
        isOfficial
      },

      // 👇 Pick the first YouTube Trailer/Teaser key
      "youtubeTrailerId": coalesce(
        trailers[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(site)=="youtube" && (lower(type)=="trailer" || lower(type)=="teaser")][0].key,
        videos[lower(platform)=="youtube" && (lower(category)=="trailer" || lower(category)=="teaser")][0].key
      )
    }`

  const results = await sanityClient.fetch(query)

  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId ?? doc._id,
    title: doc.title,
    poster_path: doc.poster ? urlFor(doc.poster).url() : '',
    backdrop_path: doc.backdrop ? urlFor(doc.backdrop).url() : '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    slug: doc.slug?.current,
    _id: doc._id,
    genres: doc.genres || [],
    showInNewReleases: doc.showInNewReleases ?? false,
    showInComingSoon: doc.showInComingSoon ?? false,
    showStreamingAPI: doc.showStreamingAPI ?? false,
    streamingLinks: doc.streamingLinks || [],
    // 👇 IMPORTANT: include the trailer id
    youtubeTrailerId: doc.youtubeTrailerId ?? null,
  }))

  return {
    data: {
      items: normalized,
      results: normalized,
      total_pages: 1,
    },
  }
}

// Add this new function to get the hero movie from Sanity
export const getHeroMovieFromSanity = async (limit = 10) => {
  const query = `*[
    _type == "bfe_movie" &&
    !(_id in path('drafts.**')) &&             // exclude drafts
    (
      isHeroMovie == true ||                   // top-level toggle
      subheading.isHeroMovie == true           // older/nested toggle (back-compat)
    )
  ]
  | order(coalesce(subheading.heroPosition, 999) asc, _updatedAt desc)[0...$limit]{
    _id,
    tmdbId,
    title,
    slug,
    releaseDate,
    overview,
    tagline,
    poster,
    backdrop,
    logo,
    voteAverage,
    voteCount,
    subheading,
    isHeroMovie,
    _updatedAt
  }`;

  const results = await sanityClientFresh.fetch(query, { limit });
  if (!results?.length) return [];

  return results.map((r: any) => ({
    id: r.tmdbId,
    title: r.title,
    overview: r.overview,
    // use urlFor so crop/hotspot are respected
    backdrop_path: r.backdrop ? urlFor(r.backdrop).url() : "",
    poster_path: r.poster ? urlFor(r.poster).url() : "",
    // 👇 NEW: Sanity-curated logo URL (prefer this in the UI)
    sanity_logo_url: r.logo ? urlFor(r.logo).url() : null,
    vote_average: r.voteAverage,
    vote_count: r.voteCount,
    release_date: r.releaseDate,
    slug: r.slug?.current,
    _id: r._id,
    subheading: r.subheading,
    isHeroMovie: r.isHeroMovie ?? r.subheading?.isHeroMovie ?? null,
    heroPosition: r.subheading?.heroPosition ?? null,
  }));
};



// Get each movie slug from Sanity
export const getMovieFromSanityBySlug = async (slug: string | undefined) => {
  const query = `*[_type == "bfe_movie" && slug.current == $slug][0]{
    _id,
    "tmdb_id": tmdbId,
    title,
    slug,
    "release_date": releaseDate,
    overview,
    tagline,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    originalLanguage,
    spokenLanguages,
    genres,
    showInNewReleases,
    showInComingSoon,
    showStreamingAPI,
    trailers[] {
      key,
      site,
      name,
      type
    },
    streamingLinks[] {
      name,
      type,
      region,
      format,
      web_url,
      ios_url,
      android_url,
      price,
      source_id,
      priority,
      isOfficial
    }
  }`

  const result = await sanityClient.fetch(query, { slug })

  if (!result) return null

  return {
    id: result.tmdbId,
    tmdb_id: result.tmdb_id,
    title: result.title,
    slug: result.slug?.current,
    release_date: result.release_date,
    overview: result.overview,
    tagline: result.tagline,
    poster_path: result.poster ? urlFor(result.poster).url() : '',
    backdrop_path: result.backdrop ? urlFor(result.backdrop).url() : '',
    vote_average: result.voteAverage,
    vote_count: result.voteCount,
    genres: result.genres,
    original_language: result.originalLanguage,
    spoken_languages: result.spokenLanguages,
    trailers: result.trailers || [],
    streamingLinks: result.streamingLinks || [],
    showInNewReleases: result.showInNewReleases ?? false,
    showInComingSoon: result.showInComingSoon ?? false,
    showStreamingAPI: result.showStreamingAPI ?? false,
  }
}

// This is sanity data and client requests and returns all data from Sanity
export const getRedHoundSanityData = async () => {
  const query = `*[_type == "redhound_movie"] | order(releaseDate desc){
    _id,
    tmdbId,
    title,
    slug,
    releaseDate,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    genres,
    showInNewReleases,
    showInComingSoon,
    showStreamingAPI,
    streamingLinks[] {
      name,
      type,
      region,
      format,
      web_url,
      ios_url,
      android_url,
      price,
      source_id,
      priority,
      isOfficial
    }
  }`

  const results = await sanityClient.fetch(query)

  // Normalize for MovieCard compatibility
  const normalized = results.map((doc: any) => ({
    id: doc.tmdbId,
    title: doc.title,
    // poster_path: doc.poster?.asset?.url || '',
    poster_path: doc.poster ? urlFor(doc.poster).url() : '',
    vote_average: doc.voteAverage,
    vote_count: doc.voteCount,
    release_date: doc.releaseDate,
    slug: doc.slug?.current,
    _id: doc._id,
    genres: doc.genres || [],
    showInNewReleases: doc.showInNewReleases ?? false,
    showInComingSoon: doc.showInComingSoon ?? false,
    showStreamingAPI: doc.showStreamingAPI ?? false,
    streamingLinks: doc.streamingLinks || [],
  }))

  return {
    data: {
      items: normalized,
      results: normalized,
      total_pages: 1,
    },
  }
}

export const getRedHoundMovieFromSanityBySlug = async (slug: string | undefined) => {
  const query = `*[_type == "redhound_movie" && slug.current == $slug][0]{
    _id,
    "tmdb_id": tmdbId,
    title,
    slug,
    "release_date": releaseDate,
    overview,
    tagline,
    poster { asset->{url} },
    backdrop { asset->{url} },
    voteAverage,
    voteCount,
    originalLanguage,
    spokenLanguages,
    genres,
    showInNewReleases,
    showInComingSoon,
    showStreamingAPI,
    trailers[] {
      key,
      site,
      name,
      type
    },
    streamingLinks[] {
      name,
      type,
      region,
      format,
      web_url,
      ios_url,
      android_url,
      price,
      source_id,
      priority,
      isOfficial
    }
  }`

  const result = await sanityClient.fetch(query, { slug })

  if (!result) return null

  return {
    id: result.tmdbId,
    tmdb_id: result.tmdb_id,
    title: result.title,
    slug: result.slug?.current,
    release_date: result.release_date,
    overview: result.overview,
    tagline: result.tagline,
    poster_path: result.poster ? urlFor(result.poster).url() : '',
    backdrop_path: result.backdrop ? urlFor(result.backdrop).url() : '',
    vote_average: result.voteAverage,
    vote_count: result.voteCount,
    genres: result.genres,
    original_language: result.originalLanguage,
    spoken_languages: result.spokenLanguages,
    trailers: result.trailers || [],
    streamingLinks: result.streamingLinks || [],
    showInNewReleases: result.showInNewReleases ?? false,
    showInComingSoon: result.showInComingSoon ?? false,
    showStreamingAPI: result.showStreamingAPI ?? false,
  }
}

export const getTrending = async (type: string, payload: object) => {
  const response = await Api().get(`/trending/all/${type}`, { params: payload })
  return response
}

// MovieApi.ts
// export const getTrending = async (
//   mediaType: 'all' | 'movie' | 'tv',
//   timeWindow: 'day' | 'week',
//   payload: object
// ) => {
//   const response = await Api().get(`/trending/${mediaType}/${timeWindow}`, {
//     params: payload
//   })
//   return response
// }

export const getNewRelease = async (type: string, payload: IGetListPayload) => {
  const response = await Api().get(`/${type}/latest`, { params: payload })
  return response
}

export const getPopular = async (type: string, payload: IGetListPayload) => {
  const response = await Api().get(`/${type}/popular`, { params: payload })
  return response
}

export const getTopRated = async (type: string, payload: IGetListPayload) => {
  const response = await Api().get(`/${type}/top_rated`, { params: payload })
  return response
}

export const getNowPlaying = async (payload: IGetListPayload) => {
  const response = await Api().get('/movie/now_playing', { params: payload })
  return response
}

export const getUpcoming = async (payload: IGetListPayload) => {
  const response = await Api().get('/movie/upcoming', { params: payload })
  return response
}

export const getAiringToday = async (payload: IGetListPayload) => {
  const response = await Api().get('/tv/airing_today', { params: payload })
  return response
}

export const getOnTheAir = async (payload: IGetListPayload) => {
  const response = await Api().get('/tv/on_the_air', { params: payload })
  return response
}

export const getDetail = async (type: string, id: string, payload: IDetailPayload) => {
  const response = await Api().get(`/${type}/${id}`, { params: payload })
  return response
}

// export const getCredits = async (type: string, id: string, payload: IDetailPayload) => {
//   const response = await Api().get(`/${type}/${id}/credits`, { params: payload })
//   return response
// }

export const getCredits = (type: string, id: string | number, params: any) =>
  tmdb.get(`/${type}/${id}/credits`, { params });

export const getReviews = (type: string, id: string | number, params: any) =>
  tmdb.get(`/${type}/${id}/reviews`, { params });

export const getTrailers = async (type: string, id: string, payload: IDetailPayload) => {
  const response = await Api().get(`/${type}/${id}/videos`, { params: payload })
  return response
}

// export const getReviews = async (type: string, id: string, payload: IDetailPayload) => {
//   const response = await Api().get(`/${type}/${id}/reviews`, { params: payload })
//   return response
// }

export const getRecommendation = async (type: string, id: string, payload: IDetailPayload) => {
  const response = await Api().get(`/${type}/${id}/recommendations`, { params: payload })
  return response
}

export const searchFilm = async (payload: object) => {
  const response = await Api().get(`search/movie`, { params: payload })
  return response
}

export const searchTv = async (payload: object) => {
  const response = await Api().get(`search/tv`, { params: payload })
  return response
}

// Get theatrical data from Airtable
export const getTheatricalData = async () => {
  try {
    const response = await backend.get('/api/airtable/records', {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.data
  } catch (error) {
    console.error('Failed to fetch theatrical data:', error)
    return { records: [] }
  }
}
